import { isContaCaixa } from "../Reforco/contaCaixaReforcoUtils";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  FormHelperText,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
  Grid,
  GridItem,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import {
  FaCalendarAlt,
  FaHospital,
  FaUser,
  FaWallet,
  FaHandHoldingUsd,
} from 'react-icons/fa';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useEffect, useState, useRef } from 'react';
import { formatCurrencyBR } from '@/utils/formatUtils';
import { useClinic } from '@/components/Context/ClinicaContext';
import { useUser } from '@/components/Context/UserContext';
import CurrencyInput from '@/components/CurrencyInput';
import { getRoleLabel } from '@/constants/userRoles';
import { request } from '@/services/fetch';
import useFinancialRefresh, { notifyFinancialUpdate } from '@/hooks/useFinancialRefresh';
import { sendSangriaOperation } from '@/services/Financas/sangriaOperation';

const OBS_MAX = 500;
const FORM_FIELD_ROW_MIN_H = '200px';

const JUSTIFICATIVA_OPTIONS = [
  { value: 'retirada_socio', label: 'Retirada de sócio / responsável' },
  { value: 'compra_mercadorias', label: 'Compra de mercadorias / diversos' },
  { value: 'deposito_conta', label: 'Depósito em Conta Corrente' },
  { value: 'outro', label: 'Outro' },
];

function SangriaModal({ children, onSuccess }) {

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [valor, setValor] = useState(0);
  const [justificativa, setJustificativa] = useState('');
  const [justificativaCustom, setJustificativaCustom] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingOperation, setPendingOperation] = useState(null);
  const sendingRef = useRef(false);
  const financialRevision = useFinancialRefresh();
  const [checkingCaixa, setCheckingCaixa] = useState(false);
  const [caixaInfo, setCaixaInfo] = useState(null);
  const [saldoDisponivel, setSaldoDisponivel] = useState(0);
  const [clinica, setClinica] = useState('');
  const [contasOrigem, setContasOrigem] = useState([]);
  const [contaOrigemId, setContaOrigemId] = useState('');
  const [loadingOrigem, setLoadingOrigem] = useState(false);
  const [erroOrigem, setErroOrigem] = useState('');
  const contaOrigem = contasOrigem.find((conta) => conta._id === contaOrigemId);
  const saldoContaOrigem = Number(contaOrigem?.saldoAtual ?? contaOrigem?.saldoInicial ?? 0);
  const [contasDestino, setContasDestino] = useState([]);
  const [contaDestinoId, setContaDestinoId] = useState('');
  const [loadingContas, setLoadingContas] = useState(false);
  const [erroContas, setErroContas] = useState('');

  const { selectedClinic } = useClinic();
  const { user } = useUser();
  const toast = useToast();

  const currentDate = new Date();
  const formattedDate = format(currentDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });

  // Helper function to get user ID from various sources
  const getUserId = () => {
    // Try from UserContext first
    if (user) {
      if (user._id) return user._id;
      if (user.id) return user.id;
      if (user.userId) return user.userId;
    }

    // Try from localStorage as fallback
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser.user) {
          return parsedUser.user._id || parsedUser.user.id;
        }
        if (parsedUser.colaborador) {
          return parsedUser.colaborador._id || parsedUser.colaborador.id;
        }
        if (parsedUser.profissionalSaude) {
          return parsedUser.profissionalSaude._id || parsedUser.profissionalSaude.id;
        }
        // Direct ID on the object
        return parsedUser._id || parsedUser.id || parsedUser.userId;
      } catch (e) {
        console.error('Erro ao processar usuario armazenado:', e);
      }
    }

    return null;
  };

  const storageKey = `osteomed:sangria-pendente:${getUserId()}:${clinica}`;
  useEffect(() => {
    if (!isOpen || !clinica) return;
    try { setPendingOperation(JSON.parse(localStorage.getItem(storageKey) || 'null')); }
    catch { setPendingOperation(null); }
  }, [isOpen, clinica, storageKey, financialRevision]);

  // Helper function to get user name
  const getUserName = () => {
    if (user) {
      return user.nome || user.name || user.nomeCompleto || 'Usuário';
    }

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser.user) {
          return parsedUser.user.nome || parsedUser.user.name || 'Usuário';
        }
        if (parsedUser.colaborador) {
          return parsedUser.colaborador.nome || parsedUser.colaborador.name || 'Colaborador';
        }
        if (parsedUser.profissionalSaude) {
          return parsedUser.profissionalSaude.nome || parsedUser.profissionalSaude.name || 'Profissional';
        }
        return parsedUser.nome || parsedUser.name || 'Usuário';
      } catch (e) {
        console.error('Erro ao processar usuario armazenado:', e);
      }
    }

    return 'Não identificado';
  };

  const getColaboradorExibicao = () => {
    if (user?.role) return getRoleLabel(user.role);
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const p = JSON.parse(stored);
        const u = p?.user || p?.colaborador || p?.profissionalSaude || p;
        const role = u?.role || p?.role;
        if (role) return getRoleLabel(role);
      }
    } catch {
      /* ignore */
    }
    return getUserName();
  };

  useEffect(() => {
    if (isOpen) {
      // Sempre usar a clínica do contexto (selecionada na Agenda/Fluxo de Caixa)
      if (selectedClinic && selectedClinic._id) {
        setClinica(selectedClinic._id);
      } else {
        setClinica('');
      }

      // Set responsible person
      setResponsavel(getUserName());
    }
  }, [isOpen, selectedClinic]);

  useEffect(() => {
    let cancelled = false;
    setContasOrigem([]);
    setContaOrigemId('');
    setErroOrigem('');
    if (!isOpen || !selectedClinic?._id) {
      setContaOrigemId('');
      return;
    }
    setLoadingOrigem(true);
    request(`api/financas/contabancaria/clinica/${selectedClinic._id}`)
      .then((contas) => {
        if (cancelled) return;
        const caixas = (Array.isArray(contas) ? contas : []).filter(isContaCaixa);
        if (caixas.length > 1) {
          setErroOrigem('Há mais de uma conta caixa cadastrada. Regularize o cadastro antes de realizar a sangria.');
          return;
        }
        const unicaConta = caixas.length === 1 && caixas[0].ativa !== false ? caixas[0] : null;
        setContasOrigem(unicaConta ? [unicaConta] : []);
        setContaOrigemId(unicaConta?._id || '');
      })
      .catch(() => { if (!cancelled) setErroOrigem('Não foi possível carregar as contas caixa. Feche e abra a sangria para tentar novamente.'); })
      .finally(() => { if (!cancelled) setLoadingOrigem(false); });
    return () => { cancelled = true; };
  }, [isOpen, selectedClinic?._id, financialRevision]);

  // Check caixa status when clinic changes
  useEffect(() => {
    if (isOpen && clinica) {
      checkCaixaStatus();
    }
  }, [isOpen, clinica, financialRevision]);

  useEffect(() => {
    let cancelled = false;
    setContaDestinoId('');
    setContasDestino([]);
    setErroContas('');
    setLoadingContas(false);
    const clinicId = selectedClinic?._id;
    if (!isOpen || !clinicId || justificativa !== 'deposito_conta') return;

    setLoadingContas(true);
    request(`api/financas/contabancaria/clinica/${clinicId}`)
      .then((contas) => {
        if (!cancelled) {
          setContasDestino(Array.isArray(contas)
            ? contas.filter((conta) => conta.ativa !== false && !isContaCaixa(conta))
            : []);
        }
      })
      .catch(() => {
        if (!cancelled) setErroContas('Não foi possível carregar as contas de destino. Selecione a justificativa novamente para tentar.');
      })
      .finally(() => {
        if (!cancelled) setLoadingContas(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, selectedClinic?._id, justificativa, financialRevision]);

  // Nome da clínica selecionada (para exibição em texto)
  const nomeClinicaSelecionada = selectedClinic
    ? (selectedClinic.razaoSocial || selectedClinic.nome || '')
    : '';

  useEffect(() => {
    // Reset form when modal closes
    if (!isOpen) {
      setValor(0);
      setJustificativa('');
      setJustificativaCustom('');
      setObservacoes('');
      setResponsavel(getUserName());
    }
  }, [isOpen]);

  const checkCaixaStatus = async () => {
    if (!clinica) return;

    try {
      setCheckingCaixa(true);

      // Check if cash register is open
      const statusData = await request(`api/financas/caixa/status/${clinica}`);

      if (!statusData.aberto && !localStorage.getItem(storageKey)) {
        toast({
          title: 'Caixa fechado',
          description: 'É necessário abrir o caixa antes de realizar uma sangria.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        onClose();
        return;
      }

      setCaixaInfo(statusData);

      // Get current cash balance
      const resumoData = await request(`api/financas/caixa/resumo-dia/${clinica}`);
      setSaldoDisponivel(resumoData.saldoCalculado || 0);
    } catch (error) {
      console.error('Erro ao verificar status do caixa:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível verificar o status do caixa.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCheckingCaixa(false);
    }
  };

  const enviarSangria = async (operation) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setLoading(true);
    setPendingOperation(operation);
    try {
      await sendSangriaOperation(operation, storageKey);
      setPendingOperation(null);
      notifyFinancialUpdate(clinica);
      toast({ title: 'Sucesso', description: 'Sangria registrada com sucesso!', status: 'success', duration: 5000, isClosable: true });
      onClose();
      Promise.resolve().then(() => onSuccess?.()).catch(console.error);
    } catch (error) {
      if (error.status === 400) setPendingOperation(null);
      else notifyFinancialUpdate(clinica);
      toast({ title: 'Sangria não confirmada', description: error.status === 400 ? error.message :
        'A confirmação está pendente. Você pode reenviar a mesma solicitação sem duplicar a retirada ou o depósito.',
        status: 'error', duration: 7000, isClosable: true });
    } finally {
      sendingRef.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (sendingRef.current) return;
    if (pendingOperation) return enviarSangria(pendingOperation);
    if (loadingOrigem || !contaOrigem) {
      toast({ title: 'A conta caixa da clínica não está disponível', status: 'warning', duration: 4000 });
      return;
    }
    if (valor > saldoContaOrigem) {
      toast({ title: 'Saldo insuficiente na conta caixa selecionada', status: 'warning', duration: 4000 });
      return;
    }
    // Validation
    if (valor <= 0) {
      toast({
        title: 'Atenção',
        description: 'Por favor, informe o valor da sangria.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (valor > saldoDisponivel) {
      toast({
        title: 'Saldo insuficiente',
        description: `O valor da sangria não pode ser maior que o saldo disponível (${formatCurrencyBR(saldoDisponivel, true)}).`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!clinica) {
      toast({
        title: 'Atenção',
        description: 'Nenhuma clínica selecionada. Selecione uma clínica no sistema antes de abrir o modal de sangria.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!justificativa) {
      toast({
        title: 'Atenção',
        description: 'Por favor, selecione uma justificativa.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (justificativa === 'outro' && !justificativaCustom) {
      toast({
        title: 'Atenção',
        description: 'Por favor, descreva a justificativa.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (justificativa === 'deposito_conta' &&
        (loadingContas || !contasDestino.some((conta) => conta._id === contaDestinoId))) {
      toast({
        title: 'Atenção',
        description: 'Selecione a conta de destino do depósito.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const userId = getUserId();
    if (!userId) {
      toast({
        title: 'Erro',
        description: 'Não foi possível identificar o usuário. Por favor, faça login novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);

      const justificativaText = justificativa === 'outro'
        ? justificativaCustom
        : JUSTIFICATIVA_OPTIONS.find(opt => opt.value === justificativa)?.label;

      const sangriaData = {
        clinica: clinica, // ID da clínica selecionada no contexto
        usuario: userId,
        tipo: 'sangria',
        contaOrigemId,
        chave: crypto.randomUUID(),
        valor: valor,
        justificativa: justificativaText,
        contaDestinoId: justificativa === 'deposito_conta' ? contaDestinoId : undefined,
        responsavel: responsavel,
        observacoes: observacoes,
        data: new Date().toISOString(),
        caixaId: caixaInfo?.ultimaAbertura?._id,
      };

      await enviarSangria({ url: 'api/financas/sangria', body: sangriaData });

    } catch (error) {
      console.error('Erro ao registrar sangria:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível registrar a sangria',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };


  const saldoApos = saldoDisponivel - valor;
  const feedbackNegativo =
    valor > 0 && (valor > saldoDisponivel || saldoApos < 0);

  const InfoRow = ({ icon, label, children }) => (
    <HStack align="flex-start" spacing={2} w="100%">
      <Icon as={icon} color="orange.500" mt={0.5} boxSize={4} flexShrink={0} />
      <Flex
        flex={1}
        justify="space-between"
        align="flex-start"
        gap={2}
        flexWrap="wrap"
        minW={0}
      >
        <Text fontWeight="semibold" fontSize="sm" color="gray.700" whiteSpace="normal">
          {label}
        </Text>
        <Text fontSize="sm" color="gray.800" textAlign={{ base: "left", md: "right" }} minW={0} overflowWrap="anywhere" flex={{ base: "1 1 100%", md: 1 }}>
          {children}
        </Text>
      </Flex>
    </HStack>
  );

  return (
    <>
      {typeof children === 'function' ? children({ onOpen }) : children}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        closeOnOverlayClick={false}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          borderRadius="lg"
          overflow="hidden"
          w={{ base: 'calc(100vw - 16px)', md: 'calc(100vw - 48px)' }}
          maxW="920px"
          my={{ base: 2, md: 6 }}
          maxH={{ base: 'calc(100dvh - 16px)', md: 'calc(100dvh - 48px)' }}
        >
          <ModalHeader
            bgGradient="linear(to-r, orange.600, orange.500)"
            color="white"
            py={2.5}
            px={4}
            pr={12}
            shadow="sm"
          >
            <HStack spacing={2}>
              <Flex
                align="center"
                justify="center"
                w={8}
                h={8}
                borderRadius="full"
                bg="whiteAlpha.300"
              >
                <Icon as={FaHandHoldingUsd} boxSize={3.5} />
              </Flex>
              <Text fontWeight="bold" fontSize="md">
                Sangria - Retirada de Caixa
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" top={2} />

          <ModalBody py={4} px={{ base: 3, md: 4 }} minH={0} overflowY="auto" overflowWrap="anywhere">
            {pendingOperation && <Alert status="warning" mb={3}><AlertIcon />
              Sangria de {formatCurrencyBR(pendingOperation.body.valor, true)} aguardando confirmação.
              O reenvio utiliza os dados originais e não duplica o lançamento.
            </Alert>}
            <VStack as="fieldset" disabled={loading || Boolean(pendingOperation)} border={0} minW={0} spacing={3} align="stretch">
              <FormControl isRequired isInvalid={Boolean(erroOrigem)}>
                <FormLabel htmlFor="sangria-conta-origem">Conta caixa de origem</FormLabel>
                <Input id="sangria-conta-origem" isReadOnly
                  value={contaOrigem ? 'Conta caixa — 000 - Conta caixa' : ''}
                  placeholder={loadingOrigem ? 'Carregando conta caixa...' : 'Conta caixa indisponível'}
                  bg={contaOrigem ? 'green.50' : 'gray.50'} borderColor={contaOrigem ? 'green.300' : 'gray.200'} />
                {contaOrigem && <Text fontSize="xs" color="gray.600" mt={1}>
                  Agência {contaOrigem.agencia || '—'} · Conta {contaOrigem.numeroConta || '—'}{contaOrigem.digitoConta ? `-${contaOrigem.digitoConta}` : ''} · Usada automaticamente
                </Text>}
                <FormHelperText>
                  {erroOrigem || (!loadingOrigem && contasOrigem.length === 0
                    ? 'Nenhuma conta ativa cadastrada com a instituição 000 - Conta caixa nesta clínica.'
                    : contaOrigem ? `Saldo da conta: ${formatCurrencyBR(saldoContaOrigem, true)}`
                      : 'O valor será retirado da conta caixa da clínica.')}
                </FormHelperText>
              </FormControl>

              {checkingCaixa ? (
                <Flex justify="center" py={6}>
                  <Spinner color="orange.500" />
                </Flex>
              ) : (
                <>
                  <Box
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={3}
                    bg="gray.50"
                  >
                    <Grid
                      templateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }}
                      gap={{ base: 3, md: 0 }}
                      alignItems="center"
                    >
                      <GridItem minW={0} pr={{ md: 4 }} pb={{ base: 3, md: 0 }}>
                        <VStack align="stretch" spacing={1.5}>
                          <InfoRow icon={FaCalendarAlt} label="Data/Hora:">
                            {formattedDate}
                          </InfoRow>
                          <InfoRow icon={FaHospital} label="Clínica:">
                            {nomeClinicaSelecionada || 'Nenhuma clínica selecionada'}
                          </InfoRow>
                          {!clinica && (
                            <Text fontSize="xs" color="orange.600" pl={6}>
                              Selecione uma clínica no seletor da tela antes de realizar a sangria.
                            </Text>
                          )}
                          <InfoRow icon={FaUser} label="Colaborador/Especialista:">
                            {getColaboradorExibicao()}
                          </InfoRow>
                        </VStack>
                      </GridItem>

                      <GridItem
                      minW={0}
                        pl={{ md: 4 }}
                        pt={{ base: 3, md: 0 }}
                        borderTopWidth={{ base: '1px', md: 0 }}
                        borderLeftWidth={{ base: 0, md: '1px' }}
                        borderColor="gray.300"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        gap={3}
                        minH={{ md: '100%' }}
                      >
                        <HStack spacing={3} align="center" justify="center">
                          <Icon
                            as={FaWallet}
                            color="orange.500"
                            boxSize={5}
                            flexShrink={0}
                          />
                          <Box textAlign="center">
                            <Text
                              fontSize="sm"
                              fontWeight="semibold"
                              color="gray.700"
                            >
                              Saldo Disponível:
                            </Text>
                            <Text
                              fontSize="xl"
                              fontWeight="bold"
                              color="green.600"
                              sx={{ fontVariantNumeric: "tabular-nums" }}
                              mt={0.5}
                              lineHeight="shorter"
                            >
                              {formatCurrencyBR(saldoDisponivel, true)}
                            </Text>
                          </Box>
                        </HStack>

                        <FormControl mb={0}>
                          <FormLabel mb={0.5} fontSize="sm">
                            Responsável pela Retirada
                          </FormLabel>
                          <Input
                            size="md"
                            value={responsavel}
                            onChange={(e) => setResponsavel(e.target.value)}
                            placeholder="Nome do responsável"
                          />
                        </FormControl>
                      </GridItem>
                    </Grid>
                  </Box>

                  <Grid
                    templateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }}
                    gap={3}
                    alignItems="stretch"
                  >
                    <GridItem
                      minW={0}
                      display="flex"
                      flexDirection="column"
                      minH={{ md: FORM_FIELD_ROW_MIN_H }}
                    >
                      <FormControl isRequired mb={0} flex="1" display="flex" flexDirection="column">
                        <FormLabel mb={0.5} fontSize="sm">
                          Valor da Sangria
                        </FormLabel>
                        <CurrencyInput
                          value={valor}
                          onChange={(v) => setValor(v)}
                          placeholder="0,00"
                        />
                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                          Digite o valor (dígitos entram pela direita, como no caixa
                          eletrônico)
                        </Text>
                      </FormControl>

                      <Alert
                        status={feedbackNegativo ? 'error' : 'info'}
                        borderRadius="md"
                        mt={2}
                        py={2}
                        px={3}
                        alignItems="flex-start"
                      >
                        <AlertIcon boxSize={4} mt={0.5} />
                        <Box fontSize="sm">
                          <Text>
                            Valor da Sangria:{' '}
                            <Text as="span" fontWeight="bold">
                              {formatCurrencyBR(valor, true)}
                            </Text>
                          </Text>
                          <Text
                            mt={1}
                            fontWeight="bold"
                            color={feedbackNegativo ? 'red.600' : 'green.700'}
                          >
                            Saldo após sangria:{' '}
                            {feedbackNegativo
                              ? `-${formatCurrencyBR(Math.abs(saldoApos), true)}`
                              : formatCurrencyBR(saldoApos, true)}
                          </Text>
                        </Box>
                      </Alert>
                    </GridItem>

                    <GridItem
                      minW={0}
                      display="flex"
                      flexDirection="column"
                      minH={{ md: FORM_FIELD_ROW_MIN_H }}
                    >
                      <FormControl isRequired h="100%" mb={0} display="flex" flexDirection="column">
                        <FormLabel mb={0.5} fontSize="sm">
                          Justificativa
                        </FormLabel>
                        <RadioGroup
                          value={justificativa}
                          onChange={setJustificativa}
                          flex="1"
                        >
                          <Stack spacing={1.5}>
                            {JUSTIFICATIVA_OPTIONS.map((option) => (
                              <Radio
                                key={option.value}
                                value={option.value}
                                size="sm"
                                colorScheme="orange"
                              >
                                <Text fontSize="sm">{option.label}</Text>
                              </Radio>
                            ))}
                          </Stack>
                        </RadioGroup>

                        {justificativa === 'outro' && (
                          <Input
                            mt={2}
                            size="sm"
                            placeholder="Descreva a justificativa..."
                            value={justificativaCustom}
                            onChange={(e) => setJustificativaCustom(e.target.value)}
                          />
                        )}
                      </FormControl>
                    </GridItem>

                    <GridItem
                      minW={0}
                      display="flex"
                      flexDirection="column"
                      minH={{ md: FORM_FIELD_ROW_MIN_H }}
                    >
                      <FormControl
                        h="100%"
                        display="flex"
                        flexDirection="column"
                        mb={0}
                        flex="1"
                      >
                        <FormLabel mb={0.5} fontSize="sm">
                          Observações
                        </FormLabel>
                        <Box position="relative" flex="1" display="flex" flexDirection="column">
                          <Textarea
                            value={observacoes}
                            onChange={(e) =>
                              setObservacoes(e.target.value.slice(0, OBS_MAX))
                            }
                            placeholder="Observações adicionais (opcional)..."
                            rows={5}
                            flex="1"
                            minH="120px"
                            size="sm"
                            maxLength={OBS_MAX}
                          />
                          <Text
                            position="absolute"
                            bottom={2}
                            right={3}
                            fontSize="xs"
                            color="gray.400"
                          >
                            {observacoes.length}/{OBS_MAX}
                          </Text>
                        </Box>
                      </FormControl>
                    </GridItem>
                  </Grid>
                  {justificativa === 'deposito_conta' && (
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Conta de destino do depósito</FormLabel>
                      <Select
                        value={contaDestinoId}
                        onChange={(e) => setContaDestinoId(e.target.value)}
                        placeholder={loadingContas ? 'Carregando contas...' : 'Selecione a conta de destino'}
                        isDisabled={loadingContas || contasDestino.length === 0}
                      >
                        {contasDestino.map((conta) => (
                          <option key={conta._id} value={conta._id}>
                            {conta.nome} — {conta.banco} — Ag. {conta.agencia} — Conta {conta.numeroConta}{conta.digitoConta ? `-${conta.digitoConta}` : ''}
                          </option>
                        ))}
                      </Select>
                      <FormHelperText color={erroContas || (!loadingContas && contasDestino.length === 0) ? 'orange.600' : 'gray.500'}>
                        {erroContas || (!loadingContas && contasDestino.length === 0
                          ? 'Nenhuma conta bancária ativa cadastrada para esta clínica.'
                          : 'Selecione a conta que receberá o dinheiro da sangria.')}
                      </FormHelperText>
                    </FormControl>
                  )}
                </>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter py={3} px={4} gap={2} flexShrink={0} flexDirection={{ base: "column", sm: "row" }} alignItems={{ base: "stretch", sm: "center" }}>
            <Button variant="outline" colorScheme="orange" size="md" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="orange"
              size="md"
              onClick={handleSubmit}
              isLoading={loading}
              isDisabled={
                !pendingOperation && (checkingCaixa || loadingOrigem || !contaOrigem || valor > saldoContaOrigem ||
                !clinica ||
                valor <= 0 ||
                valor > saldoDisponivel ||
                !justificativa ||
                (justificativa === 'outro' && !justificativaCustom) ||
                (justificativa === 'deposito_conta' &&
                  (loadingContas || !contasDestino.some((conta) => conta._id === contaDestinoId))))
              }
            >
              {pendingOperation ? 'Reenviar sangria pendente' : 'Confirmar Sangria'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default SangriaModal;
